// @figma file=tbd node=tbd name="Sonner" set="tbd" verified=tbd

import * as React from 'react';
import type { ToasterProps } from 'sonner';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';

// ─── Re-export toast utility ──────────────────────────────────────────────────

export type { ToasterProps };
export { sonnerToast as toast };

// ─── Token-mapped classNames ──────────────────────────────────────────────────
//
// Sonner applies classNames at the toast level via toastOptions.classNames.
// Each key maps to a CSS class applied to the corresponding element.
// We replace shadcn's hardcoded primitives with AvilaRoffe semantic tokens.

const defaultToastClassNames: NonNullable<NonNullable<ToasterProps['toastOptions']>['classNames']> =
  {
    toast: [
      'group toast',
      'group-[.toaster]:bg-background',
      'group-[.toaster]:text-foreground',
      'group-[.toaster]:border-border',
      'group-[.toaster]:shadow-lg',
      'group-[.toaster]:rounded-[var(--radius)]',
    ].join(' '),
    title: 'font-semibold text-sm',
    description: 'group-[.toast]:text-muted-foreground text-sm',
    actionButton: [
      'group-[.toast]:bg-primary',
      'group-[.toast]:text-primary-foreground',
      'group-[.toast]:rounded-[var(--radius)]',
      'group-[.toast]:text-xs',
      'group-[.toast]:font-medium',
      'group-[.toast]:px-2.5',
      'group-[.toast]:py-1',
    ].join(' '),
    cancelButton: [
      'group-[.toast]:bg-secondary',
      'group-[.toast]:text-secondary-foreground',
      'group-[.toast]:rounded-[var(--radius)]',
      'group-[.toast]:text-xs',
      'group-[.toast]:font-medium',
      'group-[.toast]:px-2.5',
      'group-[.toast]:py-1',
    ].join(' '),
    closeButton: [
      'group-[.toaster]:bg-background',
      'group-[.toaster]:border-border',
      'group-[.toaster]:text-foreground',
      'group-[.toaster]:rounded-[var(--radius)]',
    ].join(' '),
    // Type-specific overrides — applied on top of the base toast class
    success: [
      'group-[.toaster]:bg-success/10',
      'group-[.toaster]:text-success',
      'group-[.toaster]:border-success/30',
      '[&_[data-icon]]:text-success',
    ].join(' '),
    info: [
      'group-[.toaster]:bg-info/10',
      'group-[.toaster]:text-info',
      'group-[.toaster]:border-info/30',
      '[&_[data-icon]]:text-info',
    ].join(' '),
    warning: [
      'group-[.toaster]:bg-warning/10',
      'group-[.toaster]:text-warning',
      'group-[.toaster]:border-warning/30',
      '[&_[data-icon]]:text-warning',
    ].join(' '),
    error: [
      'group-[.toaster]:bg-destructive/10',
      'group-[.toaster]:text-destructive',
      'group-[.toaster]:border-destructive/30',
      '[&_[data-icon]]:text-destructive',
    ].join(' '),
    loading: [
      'group-[.toaster]:bg-background',
      'group-[.toaster]:text-muted-foreground',
      'group-[.toaster]:border-border',
    ].join(' '),
  };

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SonnerProps extends ToasterProps {
  /**
   * Additional CSS class applied to the Toaster wrapper element.
   */
  className?: string;
}

// ─── Toaster ──────────────────────────────────────────────────────────────────
//
// Wraps Sonner's <Toaster> with AvilaRoffe token-mapped classNames.
// All type variants (default, success, info, warning, error, loading)
// are styled via semantic tokens — no hardcoded colours.
//
// Accessibility:
// - Sonner natively applies role="status" (polite) for default/success/info/loading
// - role="alert" (assertive) for error/warning
// - aria-live="polite" | aria-live="assertive" handled internally by sonner
//
// Usage:
//   <Toaster />                          — mount once at app root
//   toast("Hello")                        — default
//   toast.success("Saved")
//   toast.error("Failed")
//   toast.warning("Be careful")
//   toast.info("FYI")
//   toast.loading("Working...")
//   toast("With action", { action: { label: "Undo", onClick: () => {} } })

const Toaster = React.forwardRef<HTMLElement, SonnerProps>(
  ({ className, toastOptions, ...props }, _ref) => (
    <SonnerToaster
      className={['toaster group', className].filter(Boolean).join(' ')}
      toastOptions={{
        ...toastOptions,
        classNames: {
          ...defaultToastClassNames,
          ...toastOptions?.classNames,
        },
      }}
      {...props}
    />
  ),
);
Toaster.displayName = 'Toaster';

export { Toaster };
